// What colour a book is, before any theme decides how to draw it.
//
// The parts here are the ones every renderer needs and none of them owns: the
// cover colour the sync worked out, and the WCAG arithmetic for deciding what
// ink is readable on it. Each theme takes it from here and then does its own
// thing - Classic nudges the tone until a title clears 4.5:1, the eight-bit
// level throws the tone away and snaps the hue to an NES swatch.
//
// They are in one file because there is no version of this that is allowed to
// drift. Two implementations of "what colour is this cover" in two themes
// would disagree about a book the day one of them was touched, and the shelf
// and the level would stop being the same shelf.

export const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

// Stable across reloads and across themes, which is what keeps a book the same
// colour in the level that it is on the shelf.
export function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// The seed a book is coloured and sized from. Title first so two editions of
// one book look alike, falling back to the id for an untitled row.
export const bookSeed = (book) => book.title || book.id || '';

// coverColour is the colour the sync took off this book's cover, or a hue
// hashed from its title when there is none yet.
//
// Returned raw, at whatever the scraper measured. Clamping belongs to the
// theme: what counts as too dark to print on depends entirely on what is being
// drawn, and a palette that quantises has no use for the clamp at all.
//
// The `computed` flag is the thing a caller must not work out for itself.
// spineColor is a pointer on the Go side precisely because black, grey and red
// all produce a zero component, so "is it zero" cannot answer "was it ever
// looked at" - see CLAUDE.md.
const FALLBACK_SATURATION = 40;
const FALLBACK_LIGHTNESS = 26;

export function coverColour(book) {
  const stored = book.spineColor;
  if (!stored) {
    return {
      hue: hashString(bookSeed(book)) % 360,
      saturation: FALLBACK_SATURATION,
      lightness: FALLBACK_LIGHTNESS,
      computed: false,
    };
  }

  return {
    hue: stored.hue,
    saturation: stored.saturation,
    lightness: stored.lightness,
    computed: true,
  };
}

export function hslToRgb(hue, saturation, lightness) {
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const secondary = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = l - chroma / 2;

  const [r, g, b] = hue < 60 ? [chroma, secondary, 0]
    : hue < 120 ? [secondary, chroma, 0]
    : hue < 180 ? [0, chroma, secondary]
    : hue < 240 ? [0, secondary, chroma]
    : hue < 300 ? [secondary, 0, chroma]
    : [chroma, 0, secondary];

  return [r, g, b].map((channel) => Math.round((channel + match) * 255));
}

// Relative luminance, the WCAG definition.
export function luminance([r, g, b]) {
  const channel = (value) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a, b) {
  const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (high + 0.05) / (low + 0.05);
}

// What a title has to clear to be worth printing. WCAG's threshold for small
// text, which a spine title is in either theme.
export const MIN_CONTRAST = 4.5;

// mostReadable picks whichever of a set of inks actually measures best on a
// colour, rather than switching at a lightness threshold.
//
// Lightness is a poor stand-in for how bright a colour looks: a vivid yellow
// at lightness 60 is far brighter than a blue at the same number, and a
// threshold put light text on both. Measuring instead fixes the whole family
// at once, and costs a little arithmetic per book.
export function mostReadable(background, inks) {
  let best = inks[0];
  let bestRatio = -1;
  for (const ink of inks) {
    const ratio = contrast(background, ink);
    if (ratio > bestRatio) {
      best = ink;
      bestRatio = ratio;
    }
  }
  return { ink: best, ratio: bestRatio };
}

export const rgbCSS = (rgb) => `rgb(${rgb.join(', ')})`;
